import { createSlice, createSelector } from "@reduxjs/toolkit";
import { coreApi } from "api/core";
import { setShowMessage } from "redux/slices/uiSlice";

const baseUrl = "/zipcodes";

const dispatchZipcodeNotUnique = (dispatch, err) => {
  const conflicts = err?.response?.data?.errors?.zipcode_not_unique_conflicts;
  if (Array.isArray(conflicts) && conflicts.length) {
    const conflictList = conflicts
      .map((c) => `${c.zipcode} (${c.supplier_name || String(c.supplier_id)})`)
      .join(", ");
    dispatch(
      setShowMessage({
        description: "zipcodeNotUniqueWithDetails",
        type: "error",
        params: { conflictList },
      })
    );
  } else {
    dispatch(
      setShowMessage({
        description: "NOT_UNIQUE",
        type: "error",
      })
    );
  }
};
const initialState = {
  zipcodes: [],
  timestamp: null,
  zipcode: null,
  loading: false,
};

const zipcodeSlice = createSlice({
  name: "zipcodes",
  initialState,
  reducers: {
    setZipcode: (state, action) => {
      state.zipcode = action.payload;
    },
    setZipcodes: (state, action) => {
      state.zipcodes = action.payload;
      state.timestamp = +new Date();
    },
    setZipcodeLoading: (state) => {
      state.loading = true;
    },
    setZipcodeReady: (state) => {
      state.loading = false;
    },
    clearZipcodes: (state) => {
      state.zipcodes = [];
      state.zipcode = null;
      state.timestamp = null;
    },
  },
});

export const { setZipcode, setZipcodes, setZipcodeLoading, setZipcodeReady, clearZipcodes } = zipcodeSlice.actions;
export default zipcodeSlice.reducer;

export const getZipcode = (id) => async (dispatch) => {
  const url = baseUrl + `/${id}`;
  dispatch(setZipcodeLoading());

  try {
    const res = await coreApi.fetch(url);
    dispatch(setZipcode(res));
  } catch (err) {
    console.error(err);
  } finally {
    dispatch(setZipcodeReady());
  }
};

export const previewZipcodeGeocode = (zipcode, country) =>
  coreApi.fetch(
    `${baseUrl}/geocode-preview?zipcode=${encodeURIComponent(zipcode)}&country=${encodeURIComponent(country)}`
  );

export const getZipcodes = () => async (dispatch) => {
  dispatch(setZipcodeLoading());

  try {
    const zipcodes = await coreApi.fetch(baseUrl);

    dispatch(setZipcodes(zipcodes));
  } catch (err) {
    console.error(err);
  } finally {
    dispatch(setZipcodeReady());
  }
};

export const addZipcode = (payload, t) => async (dispatch) => {
  dispatch(setZipcodeLoading());

  try {
    const zipcode = await coreApi.post(baseUrl, payload);
    dispatch(clearZipcodes());
    dispatch(
      setShowMessage({
        description: t("Zipcode created"),
        type: "success",
      })
    );

    return zipcode;
  } catch (err) {
    if (err?.response?.data?.errors?.zipcode === "NOT_UNIQUE") {
      dispatchZipcodeNotUnique(dispatch, err);
    } else if (err?.response?.data?.errors?.geocode === "FAILED") {
      dispatch(
        setShowMessage({
          description: "ZIPCODE_GEOCODE_FAILED",
          type: "error",
        })
      );
    } else {
      dispatch(
        setShowMessage({
          description: "There was an error creating the zipcode",
          type: "error",
        })
      );
    }

    throw err;
  } finally {
    dispatch(setZipcodeReady());
  }
};

export const addZipcodes = (payload, t) => async (dispatch) => {
  dispatch(setZipcodeLoading());

  try {
    await coreApi.post(baseUrl, payload);
    dispatch(clearZipcodes());
    dispatch(
      setShowMessage({
        description: t("Zipcodes created"),
        type: "success",
      })
    );
    return true;
  } catch (err) {
    if (err?.response?.data?.errors?.zipcode === "NOT_UNIQUE") {
      dispatchZipcodeNotUnique(dispatch, err);
    } else if (err?.response?.data?.errors?.geocode === "FAILED") {
      dispatch(
        setShowMessage({
          description: "ZIPCODE_GEOCODE_FAILED",
          type: "error",
        })
      );
    } else if (err?.response?.data?.errors?.supplier_ids === "INVALID") {
      dispatch(
        setShowMessage({
          description: t("INVALID_SUPPLIERS"),
          type: "error",
        })
      );
    } else {
      dispatch(
        setShowMessage({
          description: "There was an error creating the zipcode",
          type: "error",
        })
      );
    }
    throw err;
  } finally {
    dispatch(setZipcodeReady());
  }
};

export const editZipcode = (id, payload) => async (dispatch) => {
  const url = baseUrl + `/${id}`;
  dispatch(setZipcodeLoading());

  try {
    const res = await coreApi.put(url, payload);

    dispatch(
      setShowMessage({
        description: "Zipcode modified successfully",
        type: "success",
      })
    );

    return res;
  } catch (err) {
    if (err?.response?.data?.errors?.zipcode === "NOT_UNIQUE") {
      dispatchZipcodeNotUnique(dispatch, err);
    } else {
      dispatch(
        setShowMessage({
          description: "Failed editing zipcode.",
          type: "error",
        })
      );
    }
    throw err;
  } finally {
    dispatch(setZipcodeReady());
  }
};

export const editZipcodeGroup = (zipcode, payload) => async (dispatch) => {
  const url = `${baseUrl}/by-zipcode/${encodeURIComponent(zipcode)}`;
  dispatch(setZipcodeLoading());

  try {
    const res = await coreApi.put(url, payload);
    dispatch(
      setShowMessage({
        description: "Zipcode modified successfully",
        type: "success",
      })
    );
    await dispatch(getZipcodes());
    return res;
  } catch (err) {
    if (err?.response?.data?.errors?.zipcode === "NOT_UNIQUE") {
      dispatchZipcodeNotUnique(dispatch, err);
    } else if (err?.response?.data?.errors?.geocode === "FAILED") {
      dispatch(
        setShowMessage({
          description: "ZIPCODE_GEOCODE_FAILED",
          type: "error",
        })
      );
    } else if (err?.response?.data?.errors?.supplier_ids === "INVALID") {
      dispatch(
        setShowMessage({
          description: "INVALID_SUPPLIERS",
          type: "error",
        })
      );
    } else {
      dispatch(
        setShowMessage({
          description: "Failed editing zipcode.",
          type: "error",
        })
      );
    }
    throw err;
  } finally {
    dispatch(setZipcodeReady());
  }
};

export const bulkUpdateZipcodes = (payload) => async (dispatch) => {
  dispatch(setZipcodeLoading());
  try {
    await coreApi.post(`${baseUrl}/bulk-update`, payload);
    dispatch(
      setShowMessage({
        description: "Zipcodes updated successfully",
        type: "success",
      })
    );
    await dispatch(getZipcodes());
    return true;
  } catch (err) {
    if (err?.response?.data?.errors?.zipcode === "NOT_UNIQUE") {
      dispatchZipcodeNotUnique(dispatch, err);
    } else if (err?.response?.data?.errors?.supplier_ids === "INVALID") {
      dispatch(
        setShowMessage({
          description: "INVALID_SUPPLIERS",
          type: "error",
        })
      );
    } else {
      dispatch(
        setShowMessage({
          description: "Failed updating zipcodes",
          type: "error",
        })
      );
    }
    throw err;
  } finally {
    dispatch(setZipcodeReady());
  }
};

function normalizeZipcodeTourTargets(targets) {
  const out = [];
  const seen = new Set();
  for (const item of targets || []) {
    let zipcode;
    let tour_id;
    let country;
    if (typeof item === "string") {
      zipcode = String(item).trim();
      tour_id = null;
    } else {
      zipcode = String(item?.zipcode ?? "").trim();
      tour_id = item?.tour_id;
      country = item?.country;
    }
    if (!zipcode || tour_id == null) {
      continue;
    }
    const normalizedCountry = country
      ? String(country).trim().toUpperCase()
      : null;
    if (!normalizedCountry) {
      continue;
    }
    const key = `${zipcode}\0${tour_id}\0${normalizedCountry}`;
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    out.push({
      zipcode,
      tour_id: Number(tour_id),
      country: normalizedCountry,
    });
  }
  return out;
}

export const deleteZipcodes = (targets) => async (dispatch) => {
  const zipcodeTargets = normalizeZipcodeTourTargets(targets);
  if (!zipcodeTargets.length) {
    return;
  }

  dispatch(setZipcodeLoading());

  try {
    const targetsParam = zipcodeTargets
      .map(
        (t) =>
          `${encodeURIComponent(t.zipcode)}:${t.tour_id}:${encodeURIComponent(t.country)}`,
      )
      .join(",");
    const res = await coreApi.delete(baseUrl, {
      params: { targets: targetsParam },
    });

    dispatch(
      setShowMessage({
        description: "Zipcode deleted successfully",
        type: "success",
      })
    );

    return res;
  } catch (err) {
    const errors = err?.response?.data?.errors;
    if (errors?.default_tour === "NOT_FOUND") {
      dispatch(
        setShowMessage({
          description: "NO_DEFAULT_TOUR",
          type: "error",
        })
      );
    } else if (errors?.zipcodes === "TOO_MANY") {
      dispatch(
        setShowMessage({
          description: "ZIPCODES_DELETE_TOO_MANY",
          type: "error",
        })
      );
    } else if (errors?.zipcodes === "NOT_FOUND") {
      dispatch(
        setShowMessage({
          description: "Failed deleting zipcodes",
          type: "error",
        })
      );
    } else {
      dispatch(
        setShowMessage({
          description: "Failed deleting zipcodes",
          type: "error",
        })
      );
    }
    throw err;
  } finally {
    dispatch(setZipcodeReady());
  }
};

const zipcodeSelector = ({ zipcodes }) => zipcodes.zipcode;
const zipcodesSelector = ({ zipcodes }) => zipcodes.zipcodes;
const zipcodeStatusSelector = ({ zipcodes }) => zipcodes.loading;

export const selectZipcode = createSelector(zipcodeSelector, (zipcode) => zipcode);
export const selectZipcodes = createSelector(zipcodesSelector, (zipcodes) => zipcodes);
export const selectZipcodeStatus = createSelector(zipcodeStatusSelector, (loading) => loading);
export const selectZipcodesTimestamp = createSelector(
  ({ zipcodes }) => zipcodes.timestamp,
  (o) => o
);
